import { QueryExpr, QueryField, QueryOperation } from '@apis/Resources';
import styled from '@emotion/styled';
import {
    Overlay,
    Input,
    NumberInput,
    ActionIcon,
    Popover,
    Box,
    Tooltip,
    Space,
    Group,
    Text,
    TextInput,
    Divider,
    Button,
    Portal,
    LoadingOverlay,
    Badge,
    Select,
    createStyles,
    useMantineTheme,
    Loader,
    Sx,
} from '@mantine/core';
import { Calendar, DatePicker } from '@mantine/dates';
import { useDisclosure, useTimeout } from '@mantine/hooks';
import { SectionedPopover, SectionedPopoverToolbar } from '@root/Design/Primitives';
import { colorPalette, CustomColors, theme } from '@root/Design/Themes';
import { useNumberInputFractions } from '@root/Design/Data';
import { useDi } from '@root/Services/DI';
import { EventEmitter, useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { FormatService, INamedFormatter, NamedFormats } from '@root/Services/FormatService';
import { createContext, ForwardedRef, forwardRef, Fragment, JSXElementConstructor, KeyboardEvent, useContext, useState } from 'react';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { CalendarTime, Check, ChevronDown, Pinned, Trash, X } from 'tabler-icons-react';
import { DropdownOpener } from '../Picker/Design';
import { Picker } from '../Picker/Picker';
import { useFloatedFullText } from '../Text/FloatedFullText';
import {
    FilterItem,
    FiltersContainer,
    FilterToken,
    getCompactOperandStyles,
    getCompactValueInputPickerSx,
    LineItemCompactRow,
    LineItemCompactToken,
} from './Design';
import { GlobalSearch, GlobalFilterModel } from './GlobalSearch';
import { IValueProviderFactory } from '@root/Services/Query/QueryDatasource';
import { ExprTypeProvider, IFieldInfoProvider, IOperationInfoProvider, IQueryToken, QueryDescriptorService } from './Services';
import { DateRangeCalendar } from './DateRangeFilter';
import { asExpr, asOperation } from '@root/Services/QueryExpr';
import { DateExprPickerContent } from '../Picker/DateExprPicker';
import { EllipsisTextEl } from '@root/Design/Text';
import { VisibleSpaces } from '../Text/VisibleSpaces';

type ValueProvider = Exclude<ReturnType<IValueProviderFactory['getValueProvider']>, undefined>;
type FieldPickerRenderer = (onChange: (field: string) => void) => ReactNode;
export type ValueRendererProvider = (filterModel: FilterExpr, model: DataFilterModel) => undefined | ValueRenderer;
type ScopedValueRendererProvider = (filterModel: FilterExpr) => undefined | ValueRenderer;
type ValueRenderer = JSXElementConstructor<{ filter: FilterExpr; apply: () => void }>;
export type DataFilterValueRenderer = ValueRenderer;
type TokenProvider = (filter: FilterExpr, defaultValue: IQueryToken[]) => IQueryToken[];

export interface PickerItem {
    value: string;
    label: string;
    children?: PickerItem[];
}

interface DataFiltersProps {
    filters: QueryExpr[];
    onModelLoaded?: (model: DataFilterModel) => void;
    onChange: (filters: QueryExpr[], globalSearch: string) => void;
    fieldInfoProvider: IFieldInfoProvider;
    operationInfoProvider?: IOperationInfoProvider;
    valueProvider?: IValueProviderFactory;
    renderFieldPicker: FieldPickerRenderer;
    renderAddFilter?: (model: DataFilterModel) => ReactNode;
    leftPlaceHolder?: ReactNode;
    valueRendererProvider?: ValueRendererProvider;
    tokenProvider?: TokenProvider;
    disabled?: boolean;
    dataFiltersAsLineItem?: boolean;
    lineItemCompact?: boolean;
    globalSearchText?: string;
    showGlobalSearch?: boolean;
    filterRequested?: EventEmitter<{ field: string } | undefined>;
    showErrors?: boolean;
    align?: 'start' | 'end';
    allowPin?: boolean;
    pinnedFilters?: QueryExpr[];
    onPin?: (filters: QueryExpr[]) => void;
}
export function DataFilters({ filters, onChange, fieldInfoProvider, renderAddFilter, ...props }: DataFiltersProps) {
    const { operationInfoProvider } = props;
    const model = useMemo(() => {
        const result = new DataFilterModel();
        return result;
    }, []);
    useEvent(model.viewInvalidated);
    useEffect(() => {
        model.autoApply = !!props.dataFiltersAsLineItem;
        model.load(filters, fieldInfoProvider, operationInfoProvider, props.valueProvider, props.tokenProvider, props.pinnedFilters);
    }, [filters, fieldInfoProvider, operationInfoProvider, props.tokenProvider, props.dataFiltersAsLineItem, props.pinnedFilters]);
    useEffect(() => {
        model.globalFilter.inputText = model.globalFilter.appliedText = props.globalSearchText ?? '';
    }, [props.globalSearchText]);
    useEffect(() => props.onModelLoaded?.(model), [model]);

    useEvent(
        props.filterRequested,
        useCallback(
            (evt?: { field: string }) => {
                if (evt) {
                    model.addOrOpenFilter(evt.field);
                }
            },
            [model]
        )
    );
    useEvent(model.filtersChanged);
    useEvent(
        model.appliedFilterChanged,
        useCallback(
            (changes: QueryExpr[]) => {
                onChange(changes, model.globalFilter.appliedText);
            },
            [model, onChange]
        )
    );
    useEvent(
        model.pinnedFilterChanged,
        useCallback(
            (changes: QueryExpr[]) => {
                if (props.onPin) {
                    props.onPin(changes);
                }
            },
            [model, props.onPin]
        )
    );
    const valueRendererProvider = useCallback(
        (filter: FilterExpr) => props.valueRendererProvider?.(filter, model),
        [model, props.valueRendererProvider]
    );
    const appearanceCtx = { compact: props.lineItemCompact, showErrors: props.showErrors };
    return (
        <AppearanceCtx.Provider value={appearanceCtx}>
            <FiltersContainer mode={props.dataFiltersAsLineItem ? 'inline' : 'pill'} align={props.align ?? 'start'}>
                <Box sx={{ display: 'flex' }}>
                    {props.leftPlaceHolder}
                    <Box
                        sx={{
                            display: 'contents',
                            '> *': { opacity: props.disabled ? 0.8 : undefined, pointerEvents: props.disabled ? 'none' : undefined },
                        }}
                    >
                        {props.showGlobalSearch ? (
                            <>
                                <GlobalSearch model={model} />
                                <Space w="xs"></Space>
                            </>
                        ) : null}
                        {renderAddFilter?.(model)}
                    </Box>
                </Box>
                {model.filters.map((f, i) => (
                    <Fragment key={i}>
                        <Box sx={{ opacity: props.disabled ? 0.8 : undefined, pointerEvents: props.disabled ? 'none' : undefined }}>
                            {!props.dataFiltersAsLineItem ? (
                                <DataFilter
                                    filter={f}
                                    align={props.align ?? 'start'}
                                    renderFieldPicker={props.renderFieldPicker}
                                    valueRendererProvider={valueRendererProvider}
                                    allowPin={props.allowPin}
                                />
                            ) : (
                                <DataFilterLineItem
                                    filter={f}
                                    disabled={props.disabled}
                                    renderFieldPicker={props.renderFieldPicker}
                                    valueRendererProvider={valueRendererProvider}
                                    compact={props.lineItemCompact}
                                />
                            )}
                        </Box>
                    </Fragment>
                ))}
            </FiltersContainer>
        </AppearanceCtx.Provider>
    );
}

const AppearanceCtx = createContext<{ showErrors?: boolean; compact?: boolean }>({});
export const DataFilterAppearanceCtx = AppearanceCtx;

export function DataFilterLineItem(props: {
    filter: FilterExpr;
    renderFieldPicker: FieldPickerRenderer;
    valueRendererProvider: ScopedValueRendererProvider;
    disabled?: boolean;
    compact?: boolean;
}) {
    const { filter, renderFieldPicker, valueRendererProvider, disabled, compact } = props;
    const LineItemComponent = compact ? LineItemCompact : LineItemFullSize;
    const applyValue = useCallback(() => filter.apply(), [filter]);
    const remove = useCallback(() => filter.delete(), [filter]);
    useEvent(filter.changed);
    return (
        <LineItemComponent
            filter={filter}
            renderFieldPicker={renderFieldPicker}
            applyValue={applyValue}
            disabled={disabled}
            remove={remove}
            valueRendererProvider={valueRendererProvider}
        />
    );
}

function LineItemFullSize({
    filter,
    renderFieldPicker,
    applyValue,
    disabled,
    remove,
    valueRendererProvider,
}: {
    filter: FilterExpr;
    renderFieldPicker: FieldPickerRenderer;
    applyValue: () => void;
    disabled: boolean | undefined;
    remove: () => void;
    valueRendererProvider: ScopedValueRendererProvider;
}) {
    const ValueComponent = getValueComponent(filter, valueRendererProvider, DataFilterDisabled) ?? DataFilterDisabled;
    return (
        <Group my="lg" noWrap spacing="xs">
            <Group sx={{ flex: 1, flexWrap: 'nowrap', columnGap: 8, rowGap: 4 }}>
                <Box sx={{ minWidth: 250, flex: undefined }}>
                    <FilterFieldPicker filter={filter} renderFieldPicker={renderFieldPicker} data-atid="ChooseFilterOperator" />
                </Box>
                <Box sx={{ minWidth: 150 }}>
                    <OperationPicker filter={filter} />
                </Box>
                <Box sx={{ flex: 1 }}>{ValueComponent ? <ValueComponent filter={filter} apply={applyValue} /> : null}</Box>
            </Group>
            {!disabled && (
                <Tooltip label="Remove">
                    <ActionIcon radius="xl" onClick={remove}>
                        <Trash size={16} />
                    </ActionIcon>
                </Tooltip>
            )}
        </Group>
    );
}

function LineItemCompact({
    filter,
    renderFieldPicker,
    applyValue,
    disabled,
    remove,
    valueRendererProvider,
}: {
    filter: FilterExpr;
    renderFieldPicker: FieldPickerRenderer;
    applyValue: () => void;
    disabled: boolean | undefined;
    remove: () => void;
    valueRendererProvider: ScopedValueRendererProvider;
}) {
    const ValueComponent = getValueComponent(filter, valueRendererProvider, DataFilterCompactDisabled) ?? DataFilterCompactDisabled;
    const hasField = filter.hasField;
    const hasOperation = filter.hasOperation;
    const hasValue = filter.operationHasValue();
    const components = !hasField ? 1 : !hasValue ? 2 : 3;
    return (
        <LineItemCompactRow components={components}>
            <FilterFieldCompactPicker disabled={disabled} filter={filter} renderFieldPicker={renderFieldPicker} />
            {hasField && <OperationCompactPicker filter={filter} />}
            {hasValue && hasOperation && ValueComponent ? <ValueComponent filter={filter} apply={applyValue} /> : null}
            {!disabled && (
                <Tooltip label="Remove">
                    <ActionIcon radius="xl" onClick={remove}>
                        <Trash size={16} />
                    </ActionIcon>
                </Tooltip>
            )}
        </LineItemCompactRow>
    );
}

export function DataFilter({
    filter,
    onClose,
    align,
    renderFieldPicker,
    valueRendererProvider,
    allowPin,
}: {
    filter: FilterExpr;
    onClose?: () => void;
    align: 'start' | 'end';
    renderFieldPicker: FieldPickerRenderer;
    valueRendererProvider: ScopedValueRendererProvider;
    allowPin?: boolean;
}) {
    const tokens = filter.getTokens(filter.getExpr() as QueryExpr);
    const showOptions = useEventValue(filter.showOptions);
    const [reposition, setReposition] = useState<number>(0);
    const [editing, setEditing] = useState(false);
    const close = useCallback(() => {
        filter.showOptions.emit(false);
        if (!filter.hasApplied && !filter.pinned) {
            filter.delete();
        }
        if (!filter.hasApplied && filter.pinned) {
            filter.applyPin();
        }
        onClose?.();
    }, [filter, onClose]);
    const toggleMenu = useCallback(() => filter.showOptions.emit(!filter.showOptions.value), [filter]);
    const keyUp = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter') {
                filter.showOptions.emit(true);
            }
        },
        [filter]
    );
    const { start } = useTimeout(() => setReposition(reposition + 1), 200);
    useEffect(start, [showOptions]);
    return (
        <>
            <OptionOverlay onClick={close} enabled={!!showOptions} />
            <Popover
                withinPortal
                opened={showOptions}
                positionDependencies={[filter, reposition, filter.field]}
                position="bottom"
                withArrow
                offset={-5}
                shadow="md"
            >
                <Popover.Target>
                    <div>
                        <Tooltip multiline sx={{ maxWidth: 500 }} label={filter.getToolTip()} position="bottom">
                            <FilterItem
                                tabIndex={0}
                                onKeyUp={keyUp}
                                onClick={toggleMenu}
                                state={filter.isValid() || filter.pinned ? 'valid' : 'invalid'}
                                data-atid="FilterClosedPill"
                            >
                                {tokens.map((t, i) => (
                                    <FilterToken
                                        maxWidth={
                                            (filter.pinned && filter.isAnyValue) ||
                                            (!(t.name instanceof Array) && t.name!.toString().toLowerCase().indexOf('compliant with') > -1)
                                                ? 1000
                                                : 100
                                        }
                                        key={i}
                                    >
                                        {t.type === 'constant' && filter.isAnyValue
                                            ? null
                                            : t.name instanceof Array
                                            ? t.name.map((n, i) => (
                                                  <Fragment key={i}>
                                                      {i === 0 ? '' : ', '}
                                                      {n}
                                                  </Fragment>
                                              ))
                                            : t.name}
                                    </FilterToken>
                                ))}
                                {filter.isAnyValue ? <FilterToken>Any</FilterToken> : null}
                                <i className="ti ti-chevron-down"></i>
                            </FilterItem>
                        </Tooltip>
                    </div>
                </Popover.Target>
                <SectionedPopover>
                    <DataFilterOptions
                        valueRendererProvider={valueRendererProvider}
                        filter={filter}
                        close={close}
                        renderFieldPicker={renderFieldPicker}
                        onEditing={setEditing}
                        allowPin={allowPin}
                    />
                </SectionedPopover>
            </Popover>
            {showOptions ? <Overlay onClick={() => (!editing ? close() : null)} opacity={0} color="#000" zIndex={5} /> : null}
        </>
    );
}

export const AddFilterButton = forwardRef<HTMLButtonElement, { onClick: () => void; icon?: string }>(function AddFilterButton(
    { onClick, icon }: { onClick: () => void; icon?: string },
    ref
) {
    const theme = useMantineTheme();
    return (
        <ActionIcon
            data-atid="GlobalFilterButton"
            ref={ref}
            variant="outline"
            onClick={onClick}
            radius="xl"
            size={30}
            style={{
                borderColor: '#0002',
                color: theme.colors.primary[6],
                background: colorPalette.fffColor,
                margin: 'var(--item-margin)',
                height: '30px',
                width: '80px',
            }}
        >
            <Tooltip label="Add Filter" withArrow withinPortal position="bottom">
                <Text size={14}>
                    <i className={icon ?? 'ti ti-filter'}></i> Filter
                </Text>
            </Tooltip>
        </ActionIcon>
    );
});

export function DataFilterOptions({
    filter,
    close,
    renderFieldPicker,
    onEditing,
    valueRendererProvider,
    allowPin,
}: {
    filter: FilterExpr;
    close: () => void;
    renderFieldPicker: FieldPickerRenderer;
    onEditing: (editing: boolean) => void;
    valueRendererProvider: ScopedValueRendererProvider;
    allowPin?: boolean;
}) {
    const theme = useMantineTheme();
    const [editingField, setEditingField] = useState(false);
    const [editingOp, setEditingOp] = useState(false);
    useEffect(() => onEditing(editingField || editingOp), [editingField, editingOp]);
    useEvent(filter.changed);
    useEvent(filter.onPinChanged);
    const apply = useCallback(() => {
        filter.apply();
        close();
    }, [close, filter]);
    const remove = useCallback(() => {
        filter.delete();
        close();
    }, [filter, close]);
    const pin = useCallback(() => {
        if (filter.pinned) {
            filter.unpin();
        } else {
            filter.pin();
        }
    }, [filter]);

    const applyPin = useCallback(() => {
        filter.applyPin();
        close();
    }, [filter]);

    const ValueComponent = getValueComponent(filter, valueRendererProvider);
    const disabled = !filter.isValid() && !filter.pinned;

    const canPin = allowPin && filter.isPinnable;

    return filter.operation == 'And' ? (
        <Box sx={{ width: 325 }}>This filter cannot be edited or deleted.</Box>
    ) : (
        <>
            <SectionedPopoverToolbar style={{ marginBottom: 12 }}>
                <Group position="right">
                    <Button onClick={filter.pinned ? applyPin : apply} disabled={disabled} variant="filled" data-atid="ApplyFilterButton">
                        Apply
                    </Button>
                    <Button onClick={close} variant="outline" data-atid="CancelFilterButton">
                        Cancel
                    </Button>
                </Group>
            </SectionedPopoverToolbar>
            <Box sx={{ width: 300 }}>
                <Group position="apart">
                    <Text>Edit filter</Text>
                    <Group>
                        {canPin && (
                            <Tooltip label={filter.pinned ? 'Unpin Filter' : 'Pin Filter'} position="right" withinPortal withArrow>
                                <ActionIcon
                                    sx={{
                                        ['&:hover svg']: { stroke: theme.colors.primary[5] },
                                        transition: 'all 300ms',
                                        transform: `rotate(${filter.pinned ? 0 : 90}deg)`,
                                    }}
                                    color={filter.pinned ? 'primary.6' : 'gray.6'}
                                    onClick={pin}
                                    data-atid="PinFilterIcon"
                                >
                                    <Pinned size={16} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Tooltip label="Remove Filter" position="right" withinPortal withArrow>
                            <ActionIcon color="error" onClick={remove} data-atid="RemoveFilterTrashCan">
                                <i className="ti ti-trash"></i>
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>
                <Space h="md" />
                <FilterFieldPicker filter={filter} renderFieldPicker={renderFieldPicker} onEditing={setEditingField} />
                <Space h="md" />
                <OperationPicker filter={filter} onEditing={setEditingOp} />
                <Space h="md" />
                {ValueComponent ? <ValueComponent filter={filter} apply={apply} /> : null}
                <Space h="md" />
            </Box>
        </>
    );
}

function getValueComponent(filter: FilterExpr, valueRendererProvider: ScopedValueRendererProvider, empty: ValueRenderer = DataFilterOther) {
    const formatId = filter.getFormat();
    const type = filter.getFilterType();
    const hasValueComponent = filter.operationHasValue();
    const customValueRenderer = valueRendererProvider(filter);

    const ValueComponent = !hasValueComponent
        ? null
        : customValueRenderer
        ? customValueRenderer
        : formatId === 'percent'
        ? DataFilterPercent
        : type === 'string'
        ? DataFilterString
        : type === 'number'
        ? DataFilterNumber
        : type === 'date'
        ? DataFilterSingleDate
        : type === 'boolean'
        ? DataFilterBool
        : empty;
    return ValueComponent;
}

function FilterFieldPickerBase({
    filter,
    renderFieldPicker,
    onEditing,
    children,
}: {
    filter: FilterExpr;
    renderFieldPicker: FieldPickerRenderer;
    onEditing?: (editing: boolean) => void;
    children: (props: { error?: boolean; opened: boolean; toggle: () => void }) => ReactNode;
}) {
    const [opened, { open, close, toggle }] = useDisclosure(false);
    const onSelect = useCallback(
        (field: string) => {
            filter.setField(field);
            close();
        },
        [close, filter]
    );
    useEffect(() => onEditing?.(opened), [onEditing, opened]);
    const appearance = useContext(AppearanceCtx);
    const error = appearance.showErrors && !filter.hasField;
    return (
        <>
            <OptionOverlay onClick={close} enabled={opened} />
            <Popover opened={opened} withinPortal offset={0} shadow="md">
                <Popover.Target>{children({ error, opened, toggle })}</Popover.Target>
                <Popover.Dropdown p={0} data-atid="ChooseFilterOperator">
                    {renderFieldPicker(onSelect)}
                </Popover.Dropdown>
            </Popover>
        </>
    );
}

function FilterFieldCompactPicker({
    disabled,
    ...props
}: {
    disabled?: boolean;
    filter: FilterExpr;
    renderFieldPicker: FieldPickerRenderer;
    onEditing?: (editing: boolean) => void;
}) {
    const [fieldToken] = props.filter.getTokens();
    const text = fieldToken.name ? fieldToken.name : 'Select a field...';
    return (
        <FilterFieldPickerBase {...props}>
            {({ error, opened, toggle }) => {
                const state = error ? 'error' : opened ? 'open' : !fieldToken.name ? 'empty' : disabled ? 'disabled' : undefined;
                return (
                    <LineItemCompactToken left state={state} onClick={toggle} data-atid={`ChooseFilterField: ${text}`}>
                        {text}
                    </LineItemCompactToken>
                );
            }}
        </FilterFieldPickerBase>
    );
}

function FilterFieldPicker(props: { filter: FilterExpr; renderFieldPicker: FieldPickerRenderer; onEditing?: (editing: boolean) => void }) {
    const [fieldToken] = props.filter.getTokens();
    const text = fieldToken.name ? fieldToken.name : 'Choose field';
    return (
        <FilterFieldPickerBase {...props}>
            {({ error, opened, toggle }) => <OperandButton error={error} open={opened} onClick={toggle} text={text} atid="ChooseFilterField" />}
        </FilterFieldPickerBase>
    );
}

interface OperandButtonProps {
    text: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    multiline?: boolean;
    open: boolean;
    error?: boolean;
    atid?: string;
    loading?: boolean;
}
const OperandButton = forwardRef(
    ({ text, onClick, disabled, multiline, open, error, atid, loading }: OperandButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
        return (
            <>
                <Input
                    ref={ref}
                    component={OperandButtonEl}
                    error={error}
                    type="button"
                    disabled={disabled}
                    multiline={multiline}
                    onClick={onClick}
                    data-atid={'FilterOperandTextInput:' + text}
                >
                    <Group p={0} position="apart" noWrap spacing={0}>
                        {text}
                        <DropdownOpener state={{ opened: open, disabled, loading, error }} onClick={onClick} />
                    </Group>
                </Input>
            </>
        );
    }
);
export const DataFilterOperandButton = OperandButton;

const OperandButtonEl = styled.button<{ error?: boolean }>`
    border-color: ${(p) => (p.error ? p.theme.colors.error[6] : undefined)};
    padding-right: 4px;
`;

function OperationPickerBase({
    filter,
    onEditing,
    children,
    width,
}: {
    filter: FilterExpr;
    onEditing?: (editing: boolean) => void;
    children: (props: { opened: boolean; toggle: () => void }) => ReactNode;
    width?: string | number;
}) {
    const [opened, { close, toggle }] = useDisclosure(false);
    const selectOperation = useCallback(
        (op: string) => {
            if (filter.operation !== op) {
                filter.setOperation(op);
            }
            close();
        },
        [filter, close]
    );
    useEffect(() => onEditing?.(opened), [onEditing, opened]);
    return (
        <>
            <OptionOverlay onClick={close} enabled={opened} />
            <Popover opened={opened} width={width ? undefined : 'target'} withinPortal offset={0} shadow="md">
                <Popover.Target>{children({ opened, toggle })}</Popover.Target>
                <Popover.Dropdown p={0}>
                    <Box>
                        <Picker
                            noFilter
                            nameAccessor={(o) => o.name}
                            selections={[]}
                            minimizeHeight
                            height={300}
                            items={filter.getOperationOptions()}
                            onChange={([o]) => selectOperation(o.op)}
                            mode="single"
                            width={width ?? '100%'}
                        />
                    </Box>
                </Popover.Dropdown>
            </Popover>
        </>
    );
}

function OperationPicker(props: { filter: FilterExpr; onEditing?: (editing: boolean) => void }) {
    const { filter } = props;
    const [, operationToken] = filter.getTokens();
    const text = operationToken?.name ? operationToken.name : 'Choose operator';
    return (
        <OperationPickerBase {...props}>
            {({ opened, toggle }) => <OperandButton open={opened} disabled={!filter} onClick={toggle} text={text} />}
        </OperationPickerBase>
    );
}

function OperationCompactPicker(props: { filter: FilterExpr; onEditing?: (editing: boolean) => void }) {
    const { filter } = props;
    const [fieldToken, operationToken] = filter.getTokens();
    const text = operationToken?.name ? operationToken.name : <>&mdash;</>;
    return (
        <OperationPickerBase {...props} width={150}>
            {({ opened, toggle }) => {
                const disabled = !fieldToken?.name;
                const state = disabled ? 'disabled' : opened ? 'open' : !operationToken?.name ? 'empty' : undefined;
                return (
                    <LineItemCompactToken state={state} onClick={disabled ? undefined : toggle}>
                        {text}
                    </LineItemCompactToken>
                );
            }}
        </OperationPickerBase>
    );
}

function OptionOverlay({ onClick, enabled }: { onClick: () => void; enabled: boolean }) {
    return <Portal>{enabled ? <Overlay onClick={onClick} opacity={0} zIndex={300} /> : null}</Portal>;
}

function ApplyButton({ apply }: { apply: () => void }) {
    return (
        <Tooltip withinPortal withArrow position="right" label="Apply filter">
            <ActionIcon color="success" onClick={apply}>
                <Check strokeWidth={3} />
            </ActionIcon>
        </Tooltip>
    );
}

const useValuePickerSx = (error?: boolean, input: boolean = true, inputType?: string) => {
    const theme = useMantineTheme();
    const appearance = useContext(AppearanceCtx);
    if (appearance.compact) {
        const state = error ? 'error' : 'empty';
        let result = getCompactOperandStyles({ right: true, theme, state });
        result = input ? getCompactValueInputPickerSx(result, inputType) : result;
        return { ...result };
    } else {
        return {};
    }
};
export function DataFilterString({ filter, apply }: { filter: FilterExpr; apply: () => void }) {
    useEvent(filter.changed);
    const onChange = useCallback(
        ((e) => {
            filter.setValue(e.currentTarget.value);
        }) as React.ChangeEventHandler<HTMLInputElement>,
        []
    );
    const valueProvider = useMemo(() => filter.getValueProvider(), [filter, filter.field]);
    const preferValuePicker = filter.operation === 'eq' || filter.operation === 'ne';
    const onPickerDone = useCallback(
        (values: any[]) => {
            filter.setValue(values);
        },
        [filter]
    );

    const appearance = useContext(AppearanceCtx);
    const error = appearance.showErrors && !filter.isValid();
    const sx = useValuePickerSx(error, !preferValuePicker);

    const isHierarchy = filter.isPathHierarchy();
    const childAccessor = isHierarchy ? filter.createPathChildAccessor() : undefined;

    return preferValuePicker && valueProvider ? (
        <PickerInput
            canShowAny={filter.isAnyValue}
            error={error}
            key={filter.field}
            value={filter.value}
            onChange={onPickerDone}
            valueProvider={valueProvider!}
            showWhitespace={filter.showValueWhitespace()}
            childAccessor={childAccessor}
        />
    ) : (
        <TextInput error={error} sx={{ flex: 1, ...sx }} value={filter.value} onChange={onChange} />
    );
}

export function UtcDataFilterSingleDate(props: { filter: FilterExpr; apply: () => void }) {
    return <DataFilterSingleDate {...props} useUtc />;
}
export function DataFilterSingleDate({ filter, apply, useUtc }: { filter: FilterExpr; apply: () => void; useUtc?: boolean }) {
    useEvent(filter.changed);
    const [opened, { close, toggle }] = useToggle(false);
    const fmtSvc = useDi(FormatService);
    const convertDate = useCallback(
        (v: Date | null) => (!v ? null : useUtc ? fmtSvc.toUtcJsonShortDate(v, true) : fmtSvc.toJsonShortDate(v)),
        [useUtc]
    );
    const normalizeDateExpr = useCallback(
        (v: QueryExpr) => {
            if (v && 'Value' in v) {
                const rawDate = v.Value;
                if (!rawDate) {
                    return null;
                }
                v.Value = rawDate instanceof Date ? convertDate(rawDate) : v.Value;
            }
            return v;
        },
        [convertDate]
    );
    const updateValue = useCallback(
        (value: QueryExpr | null | [string, string], shouldClose: boolean) => {
            filter.setValue(value);
            if (shouldClose) {
                close();
            }
        },
        [close, filter]
    );
    const onBetweenValuesChange = useCallback(
        (values: null | [Date, Date]) => {
            const converted = values?.filter((v) => !!v).map((v) => convertDate(v!));
            const nextValue = converted?.length !== 2 ? null : (converted as [string, string]);
            updateValue(nextValue, !!nextValue);
        },
        [updateValue]
    );
    const onDateExprChange = useCallback(
        (value: QueryExpr | undefined) => {
            const converted = !value ? null : normalizeDateExpr(value);
            updateValue(converted, true);
        },
        [updateValue]
    );

    const values = filter.getValueAsArray();
    const { dates, texts, expr } = values
        .filter((v) => !!v)
        .reduce(
            (result, v) => {
                const dateExpr = asExpr(v);
                if (dateExpr && !('Value' in dateExpr)) {
                    result.texts.push(
                        filter
                            .getTokens(dateExpr)
                            ?.map((t) => t.text)
                            .join(' ') ?? ''
                    );
                    result.expr = dateExpr;
                } else {
                    const rawDate = dateExpr?.Value ?? v;
                    const date = fmtSvc.parseDateNoTime(rawDate!);
                    result.texts.push(filter.formatValue(date, fmtSvc.getFormatter('short-date' as NamedFormats)));
                    result.dates.push(date);
                    result.expr = { Value: date as Date };
                }
                return result;
            },
            { dates: [] as Date[], texts: [] as string[], expr: undefined as undefined | QueryExpr[] }
        );
    const text =
        filter.operation !== 'between' ? (
            <>{texts[0] || <>&nbsp;</>}</>
        ) : texts.length !== 2 ? (
            <>Select range</>
        ) : (
            <>
                {texts[0]} &ndash; {texts[1]}
            </>
        );
    const rangeValue = useMemo(() => (dates?.length === 2 ? dates : [null, null]) as [Date | null, Date | null], [JSON.stringify(dates)]);

    const appearance = useContext(AppearanceCtx);
    const error = appearance.showErrors && !filter.isValid();

    return (
        <>
            <OptionOverlay onClick={close} enabled={opened} />
            <Popover withinPortal opened={opened} onClose={close} offset={0} shadow="md">
                <Popover.Target>
                    {appearance.compact ? (
                        <LineItemCompactToken right state={opened ? 'open' : error ? 'error' : undefined} onClick={toggle}>
                            {text}
                        </LineItemCompactToken>
                    ) : (
                        <Box>
                            <OperandButton open={opened} onClick={toggle} text={text} />
                        </Box>
                    )}
                </Popover.Target>
                <Popover.Dropdown>
                    {filter.operation === 'between' ? (
                        <DateRangeCalendar value={rangeValue} onChange={onBetweenValuesChange} />
                    ) : (
                        <DateExprPickerContent mode="expr" format={filter.getFormat()} value={expr} onChange={onDateExprChange} />
                    )}
                </Popover.Dropdown>
            </Popover>
        </>
    );
}

export function DataFilterNumber({ filter }: { filter: FilterExpr; apply: () => void }) {
    useEvent(filter.changed);
    const appearance = useContext(AppearanceCtx);
    const error = appearance.showErrors && !filter.isValid();
    const sx = useValuePickerSx(error, true, 'number');
    const fractionInputProps = useNumberInputFractions(filter.value);

    return (
        <NumberInput
            sx={sx}
            {...fractionInputProps}
            error={error}
            icon={<i className="ti ti-hash"></i>}
            value={filter.value}
            onChange={(e) => filter.setValue(e)}
        />
    );
}
export function DataFilterPercent({ filter }: { filter: FilterExpr; apply: () => void }) {
    useEvent(filter.changed);
    const appearance = useContext(AppearanceCtx);
    const error = appearance.showErrors && !filter.isValid();
    const fractionInputProps = useNumberInputFractions(filter.value);
    const sx = useValuePickerSx(error, true, 'number');
    return (
        <NumberInput
            sx={sx}
            {...fractionInputProps}
            icon={<i className="ti ti-percentage"></i>}
            value={typeof filter.value === 'number' ? filter.value * 100 : filter.value}
            onChange={(e) => filter.setValue(typeof e === 'number' ? e / 100 : undefined)}
        />
    );
}
export function PercentTokenProvider(filter: FilterExpr, tokens: IQueryToken[]) {
    return tokens.map((token) => {
        if (token.type === 'constant' && typeof filter.value === 'number') {
            const text = Math.round(filter.value * 100) + '%';
            return {
                expr: token.expr,
                name: text,
                text,
                type: 'constant',
            } as IQueryToken;
        }
        return token;
    });
}
export function DataFilterBool({ filter }: { filter: FilterExpr; apply: () => void }) {
    return (
        <Select
            data={[
                { value: 'y', label: 'True' },
                { value: 'n', label: 'False' },
            ]}
            value={filter.value === true ? 'y' : filter.value === false ? 'n' : ''}
            onChange={(v) => filter.setValue(v === 'y' ? true : v === 'n' ? false : undefined)}
        />
    );
}
export function DataFilterOther({ filter }: { filter: FilterExpr; apply: () => void }) {
    return <></>;
}
export function DataFilterDisabled({ filter }: { filter: FilterExpr; apply: () => void }) {
    return <OperandButton onClick={() => {}} open={false} disabled text="&nbsp;" />;
}
export function DataFilterCompactDisabled({ filter }: { filter: FilterExpr; apply: () => void }) {
    return (
        <LineItemCompactToken right state="disabled">
            {''}
        </LineItemCompactToken>
    );
}

interface PickerInputItem {
    value: string;
    label: string;
}
export function PickerInput({
    value,
    onChange,
    valueProvider,
    canShowAny,
    error,
    showWhitespace,
    childAccessor,
}: {
    value: string | string[];
    onChange: (value: string[]) => void;
    valueProvider: ValueProvider;
    canShowAny: boolean;
    error?: boolean;
    showWhitespace?: boolean;
    childAccessor?: (item: string | PickerItem) => (string | PickerItem)[] | undefined;
}) {
    const filterState = useMemo(
        () => ({
            filter: new EventEmitter(''),
            labelLookup: new Map<string, string>(),
            getLabel(value: string) {
                return this.labelLookup.get(value) ?? value;
            },
        }),
        []
    );
    const [opened, { toggle, close }] = useDisclosure(false);
    const [items, setItems] = useState<(string | PickerItem)[]>([]);
    const [loading, setLoading] = useState(true);
    const [selections, setSelections] = useState<string[]>(value instanceof Array ? value : typeof value === 'string' ? [value] : []);
    const [showFilter, setShowFilter] = useState(false);
    const [showingMax, setShowingMax] = useState(false);
    const { classes } = useStyles();
    const renderPickerItem = useMemo(
        () =>
            showWhitespace
                ? (value: string | PickerItem) => (
                      <EllipsisTextEl>
                          <VisibleSpaces value={typeof value === 'string' ? value : value.label} />
                      </EllipsisTextEl>
                  )
                : (value: string | PickerItem) => (typeof value === 'string' ? filterState.getLabel(value) : value.label),
        [filterState, showWhitespace]
    );
    const theme = useMantineTheme();
    const badgeFloatStyle = useMemo(
        () =>
            ({
                background: '#fff',
                padding: '2px 8px',
                marginLeft: '-15px',
                marginTop: '-4px',
                borderRadius: '15px',
                fontSize: '14px',
                whiteSpace: 'normal',
                border: 'solid 1px ' + theme.colors.gray[4],
            } as CSSStyleDeclaration),
        []
    );
    const { floatOnMouseEnter: badgeMouseEnter } = useFloatedFullText(badgeFloatStyle);
    const loadItems = useCallback(
        async (filter: string = '') => {
            let items: (string | PickerItem)[];
            if (typeof valueProvider === 'function') {
                const values = await valueProvider(filter ?? '', 101);
                if (filter !== filterState.filter.value) {
                    return;
                }
                const results = values.slice(0, 100).filter((v: string | null | PickerInputItem | PickerItem) => v !== null);
                if (!filter) {
                    setShowFilter(results.length > 20);
                }

                // Check if results are already PickerItems (hierarchical data)
                if (results.length > 0 && typeof results[0] === 'object' && 'label' in results[0]) {
                    items = results as PickerItem[];
                    // Build label lookup from hierarchical items
                    const buildLabelLookup = (items: PickerItem[]) => {
                        items.forEach((item) => {
                            filterState.labelLookup.set(item.value, item.label);
                            if (item.children) {
                                buildLabelLookup(item.children);
                            }
                        });
                    };
                    buildLabelLookup(items as PickerItem[]);
                } else {
                    // Flat string items
                    items = results.map((v: string | PickerInputItem) => (typeof v === 'string' ? v : v.value));
                    filterState.labelLookup = new Map<string, string>(
                        results.map((v: string | PickerInputItem) => (typeof v === 'string' ? [v, v] : [v.value, v.label]))
                    );
                }

                setShowingMax(items.length !== 0 && values.length > items.length);
            } else {
                // Array value provider
                items = valueProvider.map((v) => v.value);
                filterState.labelLookup = new Map(valueProvider.map((v) => [v.value, v.label]));
            }
            setItems(items);
        },
        [setShowFilter, setShowingMax, setSelections, setItems, valueProvider]
    );
    const filter = useEventValue(filterState.filter);
    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                await loadItems();
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    const { start, clear } = useTimeout(
        async () => {
            loadItems(filterState.filter.value);
        },
        300,
        { autoInvoke: false }
    );
    const deselect = (item: string) => {
        apply(selections.filter((s) => s !== item));
    };
    const handleFilterChange = (text: string) => {
        filterState.filter.emit(text);
        clear();
        start();
    };
    const apply = useCallback(
        (items: (string | PickerItem)[]) => {
            const selectedValues = items.map((i) => (typeof i === 'string' ? i : i.value));
            setSelections(selectedValues);
            onChange(selectedValues);
        },
        [onChange, setSelections]
    );
    useEffect(() => {
        onChange(selections);
    }, [selections]);

    childAccessor ??= () => undefined;
    const appearance = useContext(AppearanceCtx);
    const text =
        !selections.length && canShowAny ? (
            'Any'
        ) : (
            <div>
                {selections.map((selection) => (
                    <Badge
                        key={selection}
                        px={4}
                        className={classes.badge}
                        rightSection={
                            <ActionIcon
                                component="span"
                                onClick={(e: { stopPropagation: () => void }) => {
                                    deselect(selection);
                                    e.stopPropagation();
                                }}
                                size="xs"
                            >
                                <X />
                            </ActionIcon>
                        }
                    >
                        <Box
                            onMouseOver={badgeMouseEnter}
                            sx={{ maxWidth: 225, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'none' }}
                        >
                            {filterState.getLabel(selection)}
                        </Box>
                    </Badge>
                ))}
            </div>
        );
    return (
        <>
            <OptionOverlay onClick={close} enabled={opened} />
            <Popover opened={opened} withinPortal offset={0} shadow="md">
                <Popover.Target>
                    {appearance.compact ? (
                        <LineItemCompactToken right state={opened ? 'open' : error ? 'error' : undefined} onClick={toggle}>
                            {text}
                        </LineItemCompactToken>
                    ) : (
                        <OperandButton loading={loading} open={opened} error={error} multiline text={text} onClick={toggle} />
                    )}
                </Popover.Target>
                <Popover.Dropdown p={0}>
                    <Box my={showFilter ? undefined : 0} sx={{ width: 400 }}>
                        {loading ? (
                            <Box sx={{ position: 'relative' }}>
                                <LoadingOverlay visible />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {showFilter ? (
                                    <>
                                        <TextInput
                                            size="xs"
                                            p="xs"
                                            autoFocus
                                            placeholder="Search list"
                                            value={filter}
                                            onChange={(e) => handleFilterChange(e.currentTarget.value)}
                                            data-atid="FilterTextInput"
                                        />
                                        <Divider />
                                    </>
                                ) : null}
                                <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
                                    <Picker
                                        nameAccessor={(v: string | PickerItem) => (typeof v === 'string' ? v : v.label)}
                                        items={items}
                                        childAccessor={childAccessor ?? (() => undefined)}
                                        noFilter={true}
                                        selections={selections}
                                        renderItem={renderPickerItem}
                                        height={260}
                                        minimizeHeight
                                        inlineFullText
                                        onChange={apply}
                                        width={400}
                                    />
                                </Box>
                                {showingMax ? (
                                    <>
                                        <Divider variant="dashed" />
                                        <Text color="dimmed" size="xs" px="sm" py="xs">
                                            Showing top 100 results
                                        </Text>
                                    </>
                                ) : null}
                            </Box>
                        )}
                    </Box>
                </Popover.Dropdown>
            </Popover>
        </>
    );
}

interface IExprTokenProps {
    token: IQueryToken;
    maxWidth?: number;
    inline?: boolean;
}
export const ExprToken = forwardRef<HTMLDivElement, IExprTokenProps>(function ExprToken({ token, ...props }, ref) {
    const tokenProps = {
        'data-token-type': token.type,
        ...token.flags?.reduce((acc, flag) => ({ ...acc, [`data-token-flag-${flag}`]: true }), {}),
        ...props,
    };
    return (
        <FilterToken ref={ref} {...tokenProps}>
            {token.name}
        </FilterToken>
    );
});

type ValidationIssueTypes = 'Missing Field' | 'Missing Value';
type ValidationIssue = { issue: ValidationIssueTypes; filter: FilterExpr };

export class DataFilterModel {
    public fieldInfo!: IFieldInfoProvider;
    public queryDescriptor!: QueryDescriptorService;
    public loaded = EventEmitter.empty();
    public filters: FilterExpr[] = [];
    public filtersChanged = EventEmitter.empty();
    private appliedFiltersKey = '';
    public appliedFilterChanged = new EventEmitter<QueryExpr[]>([]);
    private pinnedfiltersKey = '';
    public pinnedFilterChanged = new EventEmitter<QueryExpr[]>([]);
    public globalFilter = new GlobalFilterModel();
    public viewInvalidated = EventEmitter.empty();
    public valueProviderFactory?: IValueProviderFactory;
    public tokenProvider?: TokenProvider;
    public autoApply = false;

    public constructor() {
        this.globalFilter.onTextApplied.listen(() => this.raiseFilterChanged());
    }

    public getFilterType(expr: QueryOperation) {
        return this.queryDescriptor.getBestOperandType(expr);
    }

    public load(
        filters: QueryExpr[],
        fieldInfo: IFieldInfoProvider,
        operationInfoProvider: IOperationInfoProvider | undefined,
        valueProviderFactory: IValueProviderFactory | undefined,
        tokenProvider: TokenProvider | undefined,
        pinnedFilters?: QueryExpr[]
    ) {
        this.fieldInfo = fieldInfo;
        this.valueProviderFactory = valueProviderFactory;
        this.tokenProvider = tokenProvider;
        this.queryDescriptor = new QueryDescriptorService(
            this.fieldInfo,
            new ExprTypeProvider(this.fieldInfo, operationInfoProvider),
            operationInfoProvider
        );
        //before we load filters filter the pinned filters out of the filters by field name. This way only one instance of a filter can exist

        const serializedPinnedExprs = pinnedFilters?.map((f) => this.serializeExpr(f));
        this.filters = filters.map((f) => this.createFilterExpr(f as QueryOperation, serializedPinnedExprs)) ?? [];

        this.checkPinChanges(false);
        this.checkAppliedFilters(false);
        this.viewInvalidated.emit();
    }

    public addFilters(...filters: QueryOperation[]) {
        const filterModels = filters.map((f) => this.createFilterExpr(f));
        this.filters.push(...filterModels);
        this.viewInvalidated.emit();
        this.raiseFilterChanged();
    }

    public addEmptyFilter = (atEnd?: boolean, raise?: boolean) => {
        const model = this.createFilterExpr({});
        this.addFilter(model, atEnd);
        if (raise) {
            this.raiseFilterChanged();
        }
    };

    public addOrOpenFilter(field: string) {
        const filter = this.createDefaultOperation(field);
        if (filter) {
            const model = this.createFilterExpr(filter);
            const existingModel = this.filters.find((f) => f.field === model.field);
            if (existingModel) {
                existingModel.showOptions.emit(true);
            } else {
                this.addFilter(model);
            }
        }
    }

    public getValidationIssues() {
        return this.filters.reduce((result, item) => {
            const issue: ValidationIssueTypes | null = !item.hasField ? 'Missing Field' : !item.isValid() ? 'Missing Value' : null;
            if (issue) {
                result.push({ issue, filter: item });
            }
            return result;
        }, [] as ValidationIssue[]);
    }

    public addFieldFilter(field: string) {
        const filter = this.createDefaultOperation(field);
        if (filter) {
            const model = this.createFilterExpr(filter);
            this.addFilter(model);
        }
    }

    public createDefaultOperation(field: string) {
        const fieldExpr = { Field: field };
        const fieldType = this.queryDescriptor.getExprType(fieldExpr);
        const defaultOpExpr = this.queryDescriptor.getDefaultOperation(fieldExpr);
        const fieldMissingValueProvider = !!this.valueProviderFactory && !this.valueProviderFactory.getValueProvider(fieldExpr);
        const shouldUseContains = fieldType === 'string' && fieldMissingValueProvider;
        return shouldUseContains ? { Operation: 'contains', Operands: [fieldExpr, { Value: '' }] } : defaultOpExpr;
    }

    private addFilter(model: FilterExpr, atEnd?: boolean) {
        if (atEnd) {
            this.filters.push(model);
        } else {
            this.filters.unshift(model);
        }
        this.viewInvalidated.emit();
        model.hasApplied = false;
        model.showOptions.emit(true);
    }

    private createFilterExpr(operation: Partial<QueryOperation>, pinnedExprs?: string[]) {
        const result = new FilterExpr(operation, this.queryDescriptor, this.valueProviderFactory, this.tokenProvider);
        result.onDelete.listen(() => this.delete(result));
        result.onApply.listen(() => this.ensureFilter(result));
        result.onPinChanged.listen(() => this.checkPinChanges());
        if (pinnedExprs) {
            const serializedExpr = this.serializeExpr(operation);
            result.pinned = pinnedExprs.includes(serializedExpr);
        }
        result.autoApply = this.autoApply;
        return result;
    }

    public delete(filterExpr: FilterExpr) {
        const idx = this.filters.findIndex((f) => f === filterExpr);
        if (idx >= 0) {
            this.filters.splice(idx, 1);
        }
        this.raiseFilterChanged();
    }

    public ensureFilter(filterExpr: FilterExpr) {
        if (!this.filters.some((f) => f === filterExpr)) {
            this.filters.push(filterExpr);
        }
        this.raiseFilterChanged();
    }

    public raiseFilterChanged() {
        this.filtersChanged.emit();
        this.checkAppliedFilters();
    }

    private checkAppliedFilters(raise: boolean = true) {
        const nextFilters = this.getFilters();
        const nextFiltersKey = JSON.stringify([nextFilters, this.globalFilter.appliedText]);

        if (nextFiltersKey !== this.appliedFiltersKey) {
            if (raise) {
                this.appliedFilterChanged.emit(nextFilters);
            }
            this.appliedFiltersKey = nextFiltersKey;
        }
    }

    private checkPinChanges(raise: boolean = true) {
        const nextFilters = this.getPinnedFilters();
        const nextFiltersKey = JSON.stringify(nextFilters);
        if (nextFiltersKey !== this.pinnedfiltersKey) {
            if (raise) {
                this.pinnedFilterChanged.emit(nextFilters);
            }
            this.pinnedfiltersKey = nextFiltersKey;
        }
    }

    public getFilters() {
        const validFilters: QueryExpr[] = [];
        for (const filter of this.filters) {
            validFilters.push(filter.getExpr() as QueryExpr);
        }
        return validFilters;
    }

    public getPinnedFilters() {
        const validFilters: QueryExpr[] = [];
        for (const filter of this.filters.filter((f) => f.pinned === true)) {
            validFilters.push(filter.getExpr() as QueryExpr);
        }
        return validFilters;
    }

    private serializeExpr(expr: Partial<QueryExpr>) {
        return JSON.stringify(expr);
    }
}

export class FilterExpr {
    public value: any;
    public get hasValue() {
        return this.value !== undefined && this.value !== null;
    }
    public operation: string;
    public field: string;
    public changed = EventEmitter.empty();
    public showOptions = new EventEmitter(false);
    public onDelete = EventEmitter.empty();
    public onApply = EventEmitter.empty();
    public onPinChanged = new EventEmitter<boolean>(false);
    public hasApplied = true;
    public autoApply = false;
    public pinned = false;

    public get hasField() {
        return !!this.field;
    }
    public get hasOperation() {
        return !!this.operation;
    }
    public get isPinnable() {
        return this.hasField || this.isValid();
    }
    public get isAnyValue() {
        return this.hasField && this.pinned && this.operation !== 'ne' && !this.isValueValid();
    }

    public constructor(
        private origFilter: Partial<QueryOperation>,
        public readonly queryDescriptor: QueryDescriptorService,
        private readonly valueProviderFactory?: IValueProviderFactory,
        private readonly tokenProvider?: TokenProvider
    ) {
        this.operation = origFilter?.Operation ?? '';
        const rightOpd = origFilter?.Operands?.[1];
        this.value = !rightOpd ? null : 'Operation' in rightOpd ? rightOpd : rightOpd?.Value;
        this.field = origFilter?.Operands?.[0]?.Field ?? '';
    }

    public getValueProvider() {
        let result = this.valueProviderFactory?.getValueProvider({ Field: this.field });
        if (result && this.isPathHierarchy()) {
            const baseProvider = result;
            result = async (filterText: string, maxResults: number) => {
                const flatResults = await (typeof baseProvider === 'function' ? baseProvider(filterText, maxResults) : baseProvider);

                const filteredResults = flatResults.slice(0, maxResults).filter((v: string | null | PickerInputItem) => v !== null);
                const hierarchy = this.createHierarchyFromPaths(
                    filteredResults.map((v: string | PickerInputItem) => (typeof v === 'string' ? v : v.value))
                );

                return hierarchy;
            };
        }
        return result;
    }

    public getExpr() {
        return this.origFilter;
    }

    public getFilterType(expr?: QueryOperation) {
        return this.queryDescriptor.getBestOperandType(expr ?? this.getPendingExpr());
    }

    public getFormat() {
        return this.queryDescriptor.getFormatter(this.getPendingExpr() as unknown as QueryExpr)?.id;
    }

    public showValueWhitespace() {
        const fieldFlags = !this.field ? null : this.queryDescriptor.fieldInfoProvider.getFlags({ Field: this.field });
        return !!fieldFlags?.some((f) => f === 'integration' || f === 'tag');
    }

    public isPathHierarchy() {
        const fieldFlags = !this.field ? null : this.queryDescriptor.fieldInfoProvider.getFlags({ Field: this.field });
        return !!fieldFlags?.some((f) => f === 'path-hierarchy');
    }

    public createHierarchyFromPaths(paths: string[]): PickerItem[] {
        const root: PickerItem[] = [];

        for (const path of paths) {
            const parts = path.split('/');
            let currentLevel = root;

            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                const existingNode = currentLevel.find((node) => node.label === part);

                if (existingNode) {
                    // Node already exists, move to its children
                    if (!existingNode.children) {
                        existingNode.children = [];
                    }
                    currentLevel = existingNode.children;
                } else {
                    // Create new node
                    const isLeaf = i === parts.length - 1;
                    const newNode: PickerItem = {
                        label: part,
                        value: isLeaf ? path : `${parts.slice(0, i + 1).join('/')}/`,
                        children: isLeaf ? undefined : [],
                    };
                    currentLevel.push(newNode);

                    // If this is not the last part, move to children
                    if (!isLeaf && newNode.children) {
                        currentLevel = newNode.children;
                    }
                }
            }
        }

        return root;
    }

    public createPathChildAccessor() {
        return (item: string | PickerItem) => {
            return typeof item === 'string' ? undefined : 'children' in item ? item.children : undefined;
        };
    }

    public isValid() {
        const filter = this.getPendingExpr();
        const operands = filter.Operands?.length ?? [];
        const shouldHaveValue = this.operationHasValue();
        return (operands === 1 && !shouldHaveValue) || (operands === 2 && shouldHaveValue && this.isValueValid());
    }

    private isValueValid() {
        return (
            (this.value instanceof Array && this.value.length > 0) ||
            (!(this.value instanceof Array) && this.value !== null && this.value !== undefined && this.value !== '')
        );
    }

    public getOperationOptions() {
        const type = this.queryDescriptor.getBestOperandType(this.getPendingExpr());
        const operations = this.queryDescriptor.getOperationOptions(type, this.field);
        const result = operations.map((o) => ({ op: o, name: this.queryDescriptor.getOperationName(o, type) }));
        return result;
    }

    public operationHasValue() {
        return this.operation !== 'isNull' && this.operation !== 'isNotNull';
    }

    public getTokens(expr?: Partial<QueryExpr> & { description?: string }) {
        expr ??= this.updateQueryExpr({} as QueryOperation);
        const result =
            'description' in expr
                ? [
                      {
                          text: expr.description!,
                          name: expr.description,
                          type: 'operation' as IQueryToken['type'],
                          op: expr,
                          expr,
                      },
                  ]
                : this.queryDescriptor.getTokensWithValueProvider(expr, this.getValueNameProvider());
        return this.tokenProvider ? this.tokenProvider(this, result) : result;
    }

    public getValueAsArray() {
        return !this.hasValue ? [] : this.value instanceof Array ? this.value : [this.value];
    }

    public formatValue(value: any, fallback: INamedFormatter) {
        let formatter = this.queryDescriptor.getFormatter({ Field: this.field });
        formatter = formatter?.ufName ? formatter : fallback;
        return formatter.format(value);
    }

    public getTitleTokens() {
        return this.getTokens(this.updateQueryExpr({} as QueryOperation)).slice(0, 2);
    }

    private getValueNameProvider() {
        const valueProvider = this.valueProviderFactory?.getValueProvider({ Field: this.field });
        const nameLookup = valueProvider instanceof Array ? new Map(valueProvider.map((item) => [item.value, item.label])) : undefined;
        const nameProvider = nameLookup
            ? { getName: (value: any) => (typeof value === 'string' ? [value] : value).map((v: unknown) => nameLookup.get(v) ?? value) }
            : undefined;
        return nameProvider;
    }

    public getToolTip() {
        return this.getTokens(this.getExpr() as QueryExpr)
            .map((t) => (t.type === 'constant' && this.isAnyValue ? 'Any' : t.text))
            .join(' ');
    }

    public setField(field: string) {
        if (this.field !== field) {
            this.field = field;
            const fieldExpr = { Field: field };
            const fieldType = this.queryDescriptor.getExprType(fieldExpr);
            const defaultOp = this.queryDescriptor.getDefaultOperation(fieldExpr).Operation;
            const op = fieldType === 'string' && this.fieldMissingValueProvider(fieldExpr) ? 'contains' : defaultOp;
            this.setOperation(op);
            this.clearValue();
        }
        this.raiseChanged();
    }

    public setOperation(op: string) {
        this.operation = op;
        this.raiseChanged();
    }

    public clearValue() {
        this.value = undefined;
    }

    public setValue(value: any) {
        this.value = value;
        this.raiseChanged();
    }

    public delete = () => {
        this.onDelete.emit();
    };

    private raiseChanged() {
        this.changed.emit();
        if (this.autoApply) {
            this.apply();
        }
    }

    private fieldMissingValueProvider(fieldExpr: QueryField) {
        return !!this.valueProviderFactory && !this.valueProviderFactory.getValueProvider(fieldExpr);
    }

    private getPendingExpr() {
        return this.updateQueryExpr({} as QueryOperation);
    }

    private updateQueryExpr(expr: Partial<QueryOperation>) {
        expr.Operation = this.operation;
        expr.Operands = [{ Field: this.field }];
        if (this.hasValue && this.operation !== 'isNull' && this.operation !== 'isNotNull') {
            const valueExpr = asExpr(this.value);
            expr.Operands.push(valueExpr ?? { Value: this.value });
        }
        return expr;
    }

    public pin() {
        this.pinned = true;
        this.updateQueryExpr(this.origFilter);
        this.onPinChanged.emit(true);
    }

    public unpin() {
        this.pinned = false;
        this.onPinChanged.emit(false);
    }

    public applyPin() {
        this.origFilter = this.updateQueryExpr(this.origFilter);
        this.onApply.emit();
    }

    public apply() {
        this.updateQueryExpr(this.origFilter);
        this.onApply.emit();
        this.hasApplied = true;
    }
}

const useStyles = createStyles((theme) => ({
    badge: {
        color: theme.colors.gray[8],
        borderColor: theme.colors.gray[4],
        backgroundColor: colorPalette.white,
    },
}));
